import { Link } from "react-router-dom";
import {
  BarChart3,
  Building2,
  Calculator,
  Calendar,
  CheckCircle,
  DollarSign,
  FileText,
  Home,
  LayoutGrid,
  MapPin,
  PlusCircle,
  TrendingUp,
  UserCheck,
  Users,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { cn } from "@/shared/lib/cn";
import { getStoredAccountType } from "../auth";
import {
  statisticsDashboardSummary,
  statisticsKeyIndicators,
  statisticsMonthlyInvoiced,
  statisticsMonthlyTrend,
} from "../data/statisticsDashboard";
import { useAuditorStatistics } from "../hooks/useAuditorStatistics";
import { StatisticsTrendsSection } from "../components/statistics-widgets/StatisticsTrendsSection";
import { StatisticsInsightsSection } from "../components/statistics-widgets/StatisticsInsightsSection";
import { statisticsIndicatorRoutes } from "../navigation/dashboardRoutes";

const indicatorIcons = {
  home: Home,
  dollar: DollarSign,
  file: FileText,
  building: Building2,
  hospital: PlusCircle,
  users: Users,
};

const footerIcons = {
  trending: TrendingUp,
  dollar: DollarSign,
  calculator: Calculator,
  check: CheckCircle,
  map: MapPin,
  userCheck: UserCheck,
};

function getDashboardHomeHref() {
  const accountType = getStoredAccountType();
  if (accountType === "auditor") return "/dashboard/auditor";
  if (accountType === "hospital") return "/dashboard/hospital";
  if (accountType === "enterprise") return "/dashboard/enterprise";
  return "/dashboard/admin";
}

function formatDashboardDate(locale: string) {
  return new Intl.DateTimeFormat(locale === "fr" ? "fr-FR" : "en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date());
}

function formatNumber(value: number, locale: string) {
  return new Intl.NumberFormat(locale === "fr" ? "fr-FR" : "en-US").format(value);
}

function MiniSparkline({ values, className }: { values: number[]; className?: string }) {
  const max = Math.max(...values, 1);
  const width = 56;
  const height = 22;
  const points = values
    .map((value, index) => {
      const x = values.length === 1 ? width / 2 : (index / (values.length - 1)) * width;
      const y = height - (value / max) * (height - 4) - 2;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg width={width} height={height} className={className} aria-hidden>
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function StatisticsPage() {
  const { t, locale } = useTranslation();
  const labels = t.safecheck.statistics;
  const accountType = getStoredAccountType();
  const isAuditor = accountType === "auditor";
  const stats = useAuditorStatistics();
  const summaryData = isAuditor && stats.usingApi ? stats.summary : statisticsDashboardSummary;
  const indicatorRows =
    isAuditor && stats.usingApi ? stats.indicators : statisticsKeyIndicators;
  const { admissions, invoicedUsd, companies, hospitals, year } = summaryData;

  const summary = labels.summary
    .replace("{admissions}", formatNumber(admissions, locale))
    .replace("{invoiced}", formatNumber(invoicedUsd, locale))
    .replace("{companies}", String(companies))
    .replace("{hospitals}", String(hospitals))
    .replace("{year}", String(year));

  const sparklineValues = (isAuditor && stats.usingApi ? stats.monthlyTrend : statisticsMonthlyTrend)
    .slice(-6)
    .map((item) => item.value);

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Breadcrumbs
          homeHref={getDashboardHomeHref()}
          items={[{ label: t.safecheck.nav.statistics }]}
        />
        <Link
          to={getDashboardHomeHref()}
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-card-muted"
        >
          <LayoutGrid size={16} />
          {labels.dashboardButton}
        </Link>
      </div>

      <div className="theme-card mb-6 flex flex-wrap items-center justify-between gap-4 p-5">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-soft">
            <BarChart3 size={24} className="text-purple" strokeWidth={1.75} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">{labels.pageTitle}</h1>
            <p className="mt-0.5 text-sm text-muted">{summary}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <span className="rounded-full bg-primary-soft px-3 py-1 text-xs font-semibold text-primary">
            {isAuditor ? labels.auditBadge : labels.badge}
          </span>
          <span className="flex items-center gap-2 text-sm text-muted">
            <Calendar size={16} />
            {formatDashboardDate(locale)}
          </span>
        </div>
      </div>

      <section>
        <h2 className="mb-4 text-[11px] font-semibold uppercase tracking-wider text-muted">
          {labels.keyIndicators}
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
          {statisticsKeyIndicators.map((indicator, index) => {
            const row = indicatorRows[index] ?? indicator;
            const Icon = indicatorIcons[indicator.icon];
            const FooterIcon = footerIcons[indicator.footerIcon];
            const label = labels.indicators[indicator.id as keyof typeof labels.indicators];
            const footer =
              labels.indicatorFooters[indicator.footerKey as keyof typeof labels.indicatorFooters];

            return (
              <Link
                key={indicator.id}
                to={statisticsIndicatorRoutes[indicator.id] ?? "/dashboard/statistics"}
                className="theme-card flex items-center gap-4 p-5 transition-all hover:border-accent/40 hover:shadow-[var(--shadow)]"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-muted">{label}</p>
                  <p className="mt-0.5 text-2xl font-bold text-foreground">
                    {formatNumber(row.value, locale)}
                  </p>
                  <p
                    className={cn(
                      "mt-1 flex items-center gap-1 text-xs",
                      indicator.footerColor ?? "text-muted",
                    )}
                  >
                    <FooterIcon size={12} />
                    {footer}
                  </p>
                  {indicator.id === "admissions" && (
                    <MiniSparkline values={sparklineValues} className="mt-2 text-primary" />
                  )}
                </div>
                <div
                  className={cn(
                    "flex h-14 w-14 shrink-0 items-center justify-center rounded-full",
                    indicator.bg,
                  )}
                >
                  <Icon size={24} className={indicator.color} strokeWidth={1.75} />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <StatisticsTrendsSection
        monthlyTrend={isAuditor && stats.usingApi ? stats.monthlyTrend : undefined}
        monthlyInvoiced={isAuditor && stats.usingApi ? stats.monthlyInvoiced : undefined}
      />
      <StatisticsInsightsSection />
    </>
  );
}
